import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@lms/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@lms/ui/popover';
import { cn } from '@lms/ui/utils';
import { CheckIcon, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslations } from 'use-intl';

export interface SearchFilterSection {
  /** Heading above the items; omit for a plain list. */
  label?: string;
  /** `keywords` are searched too but not shown (e.g. an email). */
  items: { id: number; label: string; keywords?: string[] }[];
}

/**
 * Searchable multi-select for filter toolbars, optionally in sections. An
 * empty selection means everything. The selection is applied when the popover
 * closes, so ticking several items fetches once.
 */
export function SearchFilter({
  prefix,
  sections,
  selected,
  onChange,
  searchPlaceholder,
  emptyText,
  className,
}: {
  prefix: string;
  sections: SearchFilterSection[];
  selected: number[];
  onChange: (selected: number[]) => void;
  searchPlaceholder: string;
  emptyText: string;
  /** Extra classes for the trigger, e.g. its height. */
  className?: string;
}) {
  const t = useTranslations('stats');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<number[]>(selected);

  const toggle = (id: number) =>
    setDraft((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));

  const summary =
    selected.length === 0
      ? t('all')
      : selected.length === 1
        ? (sections
            .flatMap((s) => s.items)
            .find((item) => item.id === selected[0])?.label ?? '')
        : t('n_selected', { count: selected.length });

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setDraft(selected);
        else if (
          draft.length !== selected.length ||
          draft.some((id) => !selected.includes(id))
        ) {
          onChange(draft);
        }
      }}
    >
      <PopoverTrigger
        className={cn(
          'border-input bg-background ring-offset-background focus:ring-ring flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-2 focus:outline-none',
          className,
        )}
      >
        <span className="truncate text-left">
          <span className="text-muted-foreground">{prefix}: </span>
          {summary}
        </span>
        <ChevronDown className="size-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-80">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => setDraft([])}
                className="gap-2"
              >
                <Check checked={draft.length === 0} />
                {t('all')}
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            {sections.map((section, i) => (
              <CommandGroup key={section.label ?? i} heading={section.label}>
                {section.items.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.id}`}
                    keywords={item.keywords}
                    onSelect={() => toggle(item.id)}
                    className="gap-2"
                  >
                    <Check checked={draft.includes(item.id)} />
                    <span className="truncate">{item.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Check({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'border-primary flex size-4 shrink-0 items-center justify-center rounded-sm border',
        checked ? 'bg-primary text-primary-foreground' : 'opacity-50',
      )}
    >
      {checked && <CheckIcon className="size-3" />}
    </span>
  );
}
